import React from 'react'

import {omit, pick} from 'es-toolkit/compat'
import {createPortal} from 'react-dom'
import {Transition} from 'react-transition-group'

import useKeyPress from '../../hooks/use-key-press'
import useLockScrolling from '../../hooks/use-lock-scrolling'
import useMergedRefs from '../../hooks/use-merge-refs'
import cn from '../../libs/class-names'

import {CommonProps, Overwrite} from '../../types'

// This has to be synced with the transition timeouts in the styles
const TRANSITION_TIMEOUT = 0.1 * 1000

type Props = Overwrite<
  CommonProps<'div'>,
  {
    children: React.ReactNode
    in?: boolean // To stay compatible with TransitionGroup
    isOpen?: boolean
    lightbox?: boolean
    fullscreenMobile?: boolean
    closeOnOutsideClick?: boolean
    onClose?: () => void
  }
>

const ModalPortal = ({
  className,
  children,
  lightbox = false,
  fullscreenMobile = false,
  isOpen = false,
  in: transitionIn = false,
  closeOnOutsideClick = true,
  onClose,
  ref,
  ...props
}: Props) => {
  const isActuallyOpen = isOpen || transitionIn
  useLockScrolling(isActuallyOpen)

  useKeyPress(() => {
    if (isActuallyOpen && onClose) {
      onClose()
    }
  }, 'Escape')

  const modalRef = React.useRef<HTMLDivElement>(null)

  const finalRef = useMergedRefs(modalRef, ref)

  // TODO CCF-1779: Remove this workaround when removing the TransitionGroup
  // ---
  // Prevent props that should be passed to the Transition from
  // being passed to the portal to avoid "unknown property" warnings
  const transitionGroupProps = ['enter', 'exit', 'onExited']
  const transitionProps = pick(props, transitionGroupProps)
  const portalProps = omit(props, transitionGroupProps)

  return (
    <Transition
      {...transitionProps}
      nodeRef={modalRef}
      in={isActuallyOpen}
      appear
      timeout={TRANSITION_TIMEOUT}
      mountOnEnter
      unmountOnExit
      onEnter={() => modalRef.current?.scrollTop} // Force first paint before going into entering state!
    >
      {state =>
        createPortal(
          <div
            {...portalProps}
            ref={finalRef}
            className={cn('ModalPortal', {[state]: !!state, lightbox, fullscreenMobile}, className)}
          >
            <div className="ModalPortal__content">{children}</div>
            <div
              className="ModalPortal__background"
              aria-hidden
              onClick={closeOnOutsideClick ? onClose : undefined}
            />
          </div>,
          document.body
        )
      }
    </Transition>
  )
}

export default ModalPortal
